import { CreditCard, QrCode, Sparkles } from 'lucide-react';
import { motion } from 'motion/react';
import { BaseActionSheet } from './BaseActionSheet';
import Moeda from '../imports/Moeda';

interface BuyCoinsModalProps {
  isOpen: boolean;
  onClose: () => void;
  missingCoins: number;
  currentBalance: number;
  onPurchaseComplete: (coinsAdded: number) => void;
  orderPrice?: number; // The actual price user is trying to unlock (normal or exclusive)
}

export function BuyCoinsModal({
  isOpen,
  onClose,
  missingCoins,
  currentBalance,
  onPurchaseComplete,
  orderPrice,
}: BuyCoinsModalProps) {
  const pricePerCoin = 0.2;

  // Calculate actual missing coins based on order price if provided
  // Otherwise, use the passed missingCoins prop for backward compatibility
  const actualMissingCoins = orderPrice
    ? Math.max(0, orderPrice - currentBalance)
    : missingCoins;

  const completionPrice = (actualMissingCoins * pricePerCoin).toFixed(2);
  const packagePrice = '179,00';
  const packageCoins = 1200;
  const packagePricePerCoin = (179 / packageCoins).toFixed(2);
  const completionPricePerCoin = pricePerCoin.toFixed(2);

  const handlePurchaseCompletion = (coinsAdded: number) => {
    onPurchaseComplete(coinsAdded);
    onClose();
  };

  return (
    <BaseActionSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Comprar moedas"
      maxHeight="85vh"
      headerContent={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px', // gap-2
            fontSize: 'var(--text-sm)',
            color: 'var(--muted-foreground)',
          }}
        >
          <div style={{ width: '16px', height: '16px' }}>
            <Moeda />
          </div>
          <span>
            Saldo atual:{' '}
            <span
              style={{
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--foreground)',
              }}
            >
              {currentBalance} moedas
            </span>
          </span>
        </div>
      }
    >
      {/* Options Container */}
      <div
        style={{
          // space-y-6
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          padding: '0 20px 24px 20px',
        }}
      >
        {/* Option 1: Complete exact amount - Minimal & Neutral */}
        <motion.div
          style={{
            cursor: 'pointer',
            backgroundColor: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-card)',
            padding: '14px',
          }}
          whileTap={{ scale: 0.99 }}
          onClick={() => handlePurchaseCompletion(actualMissingCoins)}
        >
          {/* Header */}
          <div style={{ marginBottom: '8px' }}>
            <h3
              style={{
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--foreground)',
                marginBottom: '2px',
              }}
            >
              Completar moedas
            </h3>
            <p
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--muted-foreground)',
                lineHeight: '1.3',
              }}
            >
              Compre apenas o necessário para este pedido
            </p>
          </div>

          {/* Coins + Price */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '8px',
              paddingBottom: '8px',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '44px',
                  height: '44px',
                  backgroundColor: 'var(--card)',
                  borderRadius: '12px',
                  border: '1px solid var(--border)',
                }}
              >
                <div style={{ width: '24px', height: '24px' }}>
                  <Moeda />
                </div>
              </div>

              <div>
                <div
                  style={{
                    fontSize: 'var(--text-base)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--foreground)',
                  }}
                >
                  +{actualMissingCoins} moedas
                </div>
                <div
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--muted-foreground)',
                    marginTop: '1px',
                  }}
                >
                  R$ {completionPricePerCoin}/moeda
                </div>
              </div>
            </div>

            <div
              style={{
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--foreground)',
              }}
            >
              R$ {completionPrice.replace('.', ',')}
            </div>
          </div>

          {/* Payment method */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px',
              fontSize: 'var(--text-xs)',
              color: 'var(--muted-foreground)',
            }}
          >
            <CreditCard
              className="w-3.5 h-3.5"
              style={{ width: '14px', height: '14px' }}
            />
            <span>Cartão de crédito</span>
          </div>

          {/* CTA Button */}
          <motion.button
            style={{
              width: '100%',
              color: '#ffffff',
              padding: '12px 16px',
              backgroundColor: 'var(--kojo-blue)',
              borderRadius: 'var(--radius-button)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              minHeight: '44px',
              border: 'none',
              cursor: 'pointer',
            }}
            whileTap={{ scale: 0.98 }}
            type="button"
          >
            Completar e liberar pedido
          </motion.button>
        </motion.div>

        {/* Option 2: Package deal - PREMIUM BLUE HIGHLIGHT CARD */}
        <motion.div
          style={{
            position: 'relative',
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            borderRadius: 'var(--radius-card)',
            padding: '18px 16px 16px 16px',
            boxShadow:
              '0 8px 24px rgba(37, 99, 235, 0.25), 0 2px 8px rgba(0, 0, 0, 0.08)',
          }}
          whileTap={{ scale: 0.99 }}
          onClick={() => handlePurchaseCompletion(packageCoins)}
        >
          {/* Premium badge */}
          <div
            style={{
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              gap: '6px', // gap-1.5
              top: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: '#FFD426',
              borderRadius: '9999px',
              paddingLeft: '12px',
              paddingRight: '12px',
              paddingTop: '6px',
              paddingBottom: '6px',
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--font-weight-semibold)',
              color: '#78350f',
              boxShadow: '0 0 0 3px rgba(59, 130, 246, 1)',
              whiteSpace: 'nowrap',
            }}
          >
            <Sparkles
              className="w-3.5 h-3.5 flex-shrink-0"
              style={{ width: '14px', height: '14px', flexShrink: 0 }}
            />
            <span>Melhor custo-benefício</span>
          </div>

          {/* Header with extra top spacing for tag */}
          <div style={{ marginBottom: '12px', marginTop: '8px' }}>
            <h3
              style={{
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-weight-semibold)',
                color: '#ffffff',
                marginBottom: '4px',
                letterSpacing: '-0.01em',
              }}
            >
              Pacote de moedas
            </h3>
            <p
              style={{
                fontSize: 'var(--text-xs)',
                color: 'rgba(255, 255, 255, 0.85)',
                lineHeight: '1.4',
              }}
            >
              Economize e tenha moedas para pedidos futuros
            </p>
          </div>

          {/* Coins + Price */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px',
              paddingBottom: '12px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '44px',
                  height: '44px',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: '12px',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                }}
              >
                <div style={{ width: '24px', height: '24px' }}>
                  <Moeda />
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: '#ffffff',
                    lineHeight: '1.2',
                    letterSpacing: '-0.02em',
                  }}
                >
                  +{packageCoins} moedas
                </div>
                <div
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: '#86efac',
                    fontWeight: 'var(--font-weight-medium)',
                    marginTop: '2px',
                  }}
                >
                  R$ {packagePricePerCoin}/moeda
                </div>
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: 'var(--text-xl)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: '#ffffff',
                  lineHeight: '1.2',
                  letterSpacing: '-0.02em',
                }}
              >
                R$ {packagePrice}
              </div>
            </div>
          </div>

          {/* Payment methods */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px', // gap-4
              marginBottom: '12px',
              fontSize: 'var(--text-xs)',
              color: 'rgba(255, 255, 255, 0.85)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px', // gap-1.5
              }}
            >
              <CreditCard
                className="w-3.5 h-3.5"
                style={{ width: '14px', height: '14px' }}
              />
              <span>Cartão</span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px', // gap-1.5
              }}
            >
              <QrCode
                className="w-3.5 h-3.5"
                style={{ width: '14px', height: '14px' }}
              />
              <span>PIX</span>
            </div>
          </div>

          {/* CTA Button */}
          <motion.button
            style={{
              width: '100%',
              padding: '12px 16px',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              color: '#2563eb',
              borderRadius: 'var(--radius-button)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-semibold)',
              minHeight: '48px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              letterSpacing: '-0.01em',
            }}
            whileTap={{ scale: 0.98 }}
            type="button"
          >
            Comprar pacote de {packageCoins} moedas
          </motion.button>
        </motion.div>
      </div>
    </BaseActionSheet>
  );
}
